import React from "react";
import { X, Trophy, Star, Scales } from "@phosphor-icons/react";

export function ComparisonModal({
  show,
  onClose,
  comparisonData,
  currentCategory,
  formatPrice
}) {
  if (!show || !comparisonData) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#111827] border border-[#1F2937] w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#111827] border-b border-[#1F2937] p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-serif text-xl text-white flex items-center gap-2">
              <Scales size={24} className="text-[#0F766E]" />
              Mart Price Comparison
            </h2>
            <p className="text-sm text-[#9CA3AF] mt-1">
              {currentCategory?.name} • Last {comparisonData.weeks_compared} weeks
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-[#9CA3AF] hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Best Overall */}
        <div className="p-4 bg-[#0F766E]/20 border-b border-[#1F2937]">
          <div className="flex items-center gap-3">
            <Trophy size={28} className="text-[#FCD34D]" />
            <div>
              <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">Best Overall</p>
              <p className="text-white font-serif text-lg">
                {comparisonData.overall_best.mart_name}
                <span className="text-[#9CA3AF] text-sm ml-2">({comparisonData.overall_best.county})</span>
              </p>
              <p className="text-[#14B8A6] text-sm">
                Top price in {comparisonData.overall_best.weeks_as_best} of {comparisonData.weeks_compared} weeks
              </p>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="p-4 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1F2937]">
                <th className="text-left px-4 py-3 text-xs font-mono uppercase text-[#9CA3AF] sticky left-0 bg-[#111827]">Mart</th>
                <th className="text-center px-3 py-3 text-xs font-mono uppercase text-[#4B5563]">Day</th>
                {comparisonData.week_labels.map((week) => (
                  <th key={week} className="text-center px-3 py-3 text-xs font-mono uppercase text-[#9CA3AF] min-w-[80px]">{week}</th>
                ))}
                <th className="text-center px-3 py-3 text-xs font-mono uppercase text-[#FCD34D] bg-[#1F2937]">Avg</th>
                <th className="text-center px-3 py-3 text-xs font-mono uppercase text-[#FCD34D] bg-[#1F2937]">Best</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {comparisonData.marts.map((mart) => {
                const stats = comparisonData.overall_stats.find(s => s.mart_name === mart.mart_name);
                const isBest = comparisonData.overall_best.mart_name === mart.mart_name;
                return (
                  <tr key={mart.mart_name} className={`hover:bg-[#F9FAFB]/50 ${isBest ? 'bg-[#0F766E]/10' : ''}`}>
                    <td className="px-4 py-3 sticky left-0 bg-[#111827]">
                      <div className="flex items-center gap-2">
                        {isBest && <Trophy size={14} className="text-[#FCD34D]" />}
                        <div>
                          <span className="text-white text-sm font-medium">{mart.mart_name}</span>
                          <span className="text-[#6B7280] text-xs ml-2">{mart.county}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-[#9CA3AF]">{mart.market_day}</td>
                    {mart.history.map((week, weekIdx) => {
                      const isBestThisWeek = comparisonData.best_per_week[weekIdx]?.best_mart === mart.mart_name;
                      return (
                        <td key={`${mart.mart_name}-week-${weekIdx}`} className={`px-3 py-3 text-center ${isBestThisWeek ? 'bg-[#059669]/20' : ''}`}>
                          <span className={`font-mono text-sm ${isBestThisWeek ? 'text-[#34D399] font-bold' : 'text-white'}`}>
                            {formatPrice(week.price, comparisonData.unit)}
                          </span>
                          {isBestThisWeek && <Star size={10} className="inline ml-1 text-[#FCD34D]" weight="fill" />}
                        </td>
                      );
                    })}
                    <td className="px-3 py-3 text-center bg-[#1F2937]/50">
                      <span className="font-mono text-sm text-[#60A5FA]">{formatPrice(stats?.avg_price, comparisonData.unit)}</span>
                    </td>
                    <td className="px-3 py-3 text-center bg-[#1F2937]/50">
                      <span className="font-mono text-sm text-[#FCD34D]">{stats?.weeks_as_best || 0}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#1F2937] flex justify-end">
          <button onClick={onClose} className="px-6 py-2 bg-[#0F766E] text-white font-sans hover:bg-[#14B8A6]">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ComparisonModal;
